import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  IndianRupee,
  Target,
  Loader2,
  CheckCircle2,
  Clock,
  LayoutGrid,
  List
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { dealsAPI, demosAPI, plansAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { cn } from '../../lib/utils';
import DealsTable from './DealsTable';
import EditDealDialog from '../../components/deals/EditDealDialog';

// All deal stages from backend
const ALL_DEAL_STAGES = [
  { value: 'POST_DEMO_FOLLOW_UP', label: 'Post Demo Follow-up', color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
  { value: 'PROPOSAL_SHARED', label: 'Proposal Shared', color: 'bg-purple-500', textColor: 'text-purple-700', bgColor: 'bg-purple-50' },
  { value: 'NEGOTIATION_IN_PROGRESS', label: 'Negotiation', color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50' },
  { value: 'PROCUREMENT_APPROVAL_PENDING', label: 'Procurement Pending', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
  { value: 'ON_HOLD_BAD_TIMING', label: 'On Hold', color: 'bg-slate-500', textColor: 'text-slate-700', bgColor: 'bg-slate-50' },
  { value: 'PENDING_ADMIN_APPROVAL', label: 'Done (Request Approval)', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50' },
  { value: 'WON_PAYMENT_PENDING', label: 'Won (Payment Pending)', color: 'bg-emerald-400', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50' },
  { value: 'WON_PAYMENT_RECEIVED', label: 'Won (Paid)', color: 'bg-emerald-600', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50' },
  { value: 'IMPLEMENTATION_IN_PROGRESS', label: 'Implementation', color: 'bg-cyan-500', textColor: 'text-cyan-700', bgColor: 'bg-cyan-50' },
  { value: 'LIVE_ACTIVE', label: 'Live & Active', color: 'bg-green-600', textColor: 'text-green-700', bgColor: 'bg-green-50' },
  { value: 'CHURNED_EARLY', label: 'Churned Early', color: 'bg-red-400', textColor: 'text-red-700', bgColor: 'bg-red-50' },
  { value: 'LOST_PRICE', label: 'Lost - Price', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' },
  { value: 'LOST_TO_COMPETITOR', label: 'Lost - Competitor', color: 'bg-red-600', textColor: 'text-red-700', bgColor: 'bg-red-50' },
  { value: 'LOST_NO_DECISION', label: 'Lost - No Decision', color: 'bg-gray-500', textColor: 'text-gray-700', bgColor: 'bg-gray-50' },
];

// Kanban view stages (subset for board view)
const KANBAN_STAGES = [
  'POST_DEMO_FOLLOW_UP',
  'PROPOSAL_SHARED', 
  'NEGOTIATION_IN_PROGRESS',
  'PENDING_ADMIN_APPROVAL',
  'WON_PAYMENT_PENDING',
  'WON_PAYMENT_RECEIVED'
];

const GST_RATE = 0.18;

const getStageInfo = (stage) => {
  return ALL_DEAL_STAGES.find(s => s.value === stage) || { 
    value: stage, 
    label: stage?.replace(/_/g, ' '), 
    color: 'bg-slate-500',
    textColor: 'text-slate-700',
    bgColor: 'bg-slate-50'
  };
};

const DealsPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const isAdmin = currentUser?.role === 'ADMIN';
  const isManager = currentUser?.role === 'MANAGER' || currentUser?.role === 'SALES_MANAGER';
  const canDelete = isAdmin || isManager;
  const canApprove = isAdmin || isManager;

  const [deals, setDeals] = useState([]);
  const [demos, setDemos] = useState([]);
  const [availableSQDs, setAvailableSQDs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('ALL');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  
  const [viewMode, setViewMode] = useState('list'); // Default to LIST as per request
  
  // Dialogs
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  
  // Selected Item States
  const [dealToDelete, setDealToDelete] = useState(null);
  const [dealToApprove, setDealToApprove] = useState(null);
  const [dealToEdit, setDealToEdit] = useState(null);
  
  const [loadingSQD, setLoadingSQD] = useState(false);

  // Initial Data Fetch
  const fetchData = async () => {
    try {
      setLoading(true);
      const [dealsRes, demosRes, plansRes, usersRes, availableSQDRes] = await Promise.all([
        dealsAPI.list({ page_size: 100 }),
        demosAPI.list({ page_size: 100 }),
        plansAPI.list(),
        usersAPI.list(),
        demosAPI.availableSQD()
      ]);
      setDeals(Array.isArray(dealsRes.data) ? dealsRes.data : (dealsRes.data.deals || []));
      setDemos(Array.isArray(demosRes.data) ? demosRes.data : (demosRes.data.demos || []));
      setPlans(plansRes.data || []);
      setUsers(usersRes.data || []);
      setAvailableSQDs(availableSQDRes.data?.available_sqds || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch SQDs on Create Modal Open
  const fetchAvailableSQDs = async () => {
    try {
      setLoadingSQD(true);
      const res = await demosAPI.availableSQD();
      setAvailableSQDs(res.data?.available_sqds || []);
    } catch (error) {
      console.error('Failed to refresh SQDs:', error);
    } finally {
      setLoadingSQD(false);
    }
  };

  useEffect(() => {
    if (isCreateModalOpen) {
        fetchAvailableSQDs();
    }
  }, [isCreateModalOpen]);
  
  // New deal form with line items
  const [newDeal, setNewDeal] = useState({
    demo_id: '',
    plan_id: '',
    total_users: 0,
    extra_user_rate: 0,
    addon_items: [],
    one_time_setup_charge: 0,
    notes: ''
  });
  
  const [addonItem, setAddonItem] = useState({ name: '', price: 0 });

  // Get selected plan details
  const selectedPlan = plans.find(p => p.id === newDeal.plan_id);
  
  // Calculate extra users
  const baseCapacity = selectedPlan?.base_user_capacity || 0;
  const extraUsers = Math.max(0, newDeal.total_users - baseCapacity);
  const extraUserRate = newDeal.extra_user_rate || 0;

  // Calculate totals with proper plan pricing
  const calculateTotals = () => {
    const basePlanPrice = selectedPlan?.base_price_per_month || 0;
    const extraUsersCost = extraUsers * extraUserRate;
    const planAmount = basePlanPrice + extraUsersCost;
    const addonsTotal = newDeal.addon_items.reduce((sum, item) => sum + item.price, 0);
    const subtotal = planAmount + addonsTotal + newDeal.one_time_setup_charge;
    const gstAmount = subtotal * GST_RATE;
    const grandTotal = subtotal + gstAmount;
    return { basePlanPrice, extraUsersCost, planAmount, addonsTotal, subtotal, gstAmount, grandTotal };
  };

  const totals = calculateTotals();

  const filteredDeals = useMemo(() => {
    return deals.filter(deal => {
      if (stageFilter !== 'all' && deal.deal_stage !== stageFilter) return false;

      if (assignedFilter !== 'ALL') {
        const dealEmp = deal.primary_employee_id;
        if (dealEmp !== assignedFilter) return false;
      }

      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        const sCompany = deal.company_name?.toLowerCase() || '';
        const sContact = deal.contact_name?.toLowerCase() || '';
        const sId = deal.id?.toLowerCase() || '';
        if (!(sCompany.includes(search) || sContact.includes(search) || sId.includes(search))) return false;
      }

      if (dateRange.from) {
        const dealDate = new Date(deal.created_at);
        if (dealDate < new Date(dateRange.from)) return false;
      }
      if (dateRange.to) {
         // Add 1 day to include end date fully
         const toDate = new Date(dateRange.to);
         toDate.setHours(23, 59, 59);
         const dealDate = new Date(deal.created_at);
         if (dealDate > toDate) return false;
      }

      return true;
    });
  }, [deals, searchQuery, stageFilter, assignedFilter, dateRange]);

  const dealsByStage = useMemo(() => {
    return KANBAN_STAGES.map(stage => ({
      stage,
      stageInfo: getStageInfo(stage),
      deals: filteredDeals.filter(d => d.deal_stage === stage)
    }));
  }, [filteredDeals]);

  const totalPipeline = filteredDeals.reduce((sum, d) => sum + (d.deal_value_estimate || 0), 0);
  const wonDeals = filteredDeals.filter(d => d.deal_stage?.startsWith('WON'));
  const pendingApprovalDeals = filteredDeals.filter(d => d.deal_stage === 'PENDING_ADMIN_APPROVAL');
  
  const avgCloseTime = useMemo(() => {
    const completedDeals = deals.filter(d => 
      ['WON_PAYMENT_RECEIVED', 'WON_PAYMENT_PENDING', 'LIVE_ACTIVE'].includes(d.deal_stage) &&
      d.created_at && d.updated_at
    );
    if (completedDeals.length === 0) return 0;
    
    const totalDays = completedDeals.reduce((sum, d) => {
      const created = new Date(d.created_at);
      const closed = new Date(d.updated_at);
      const diffTime = Math.abs(closed - created);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return sum + diffDays;
    }, 0);
    
    return Math.round(totalDays / completedDeals.length);
  }, [deals]);

  const handleSelectPlan = (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (plan) {
      setNewDeal({
        ...newDeal,
        plan_id: planId,
        price_per_user: plan.base_price_per_month || plan.base_price_per_user
      });
    }
  };

  const addAddonItem = () => {
    if (addonItem.name && addonItem.price > 0) {
      setNewDeal({
        ...newDeal,
        addon_items: [...newDeal.addon_items, { ...addonItem, id: Date.now() }]
      });
      setAddonItem({ name: '', price: 0 });
    }
  };

  const removeAddonItem = (id) => {
    setNewDeal({
      ...newDeal,
      addon_items: newDeal.addon_items.filter(item => item.id !== id)
    });
  };

  const handleCreateDeal = async () => {
    try {
      const selectedDemo = demos.find(d => d.id === newDeal.demo_id);
      const companyName = selectedDemo?.company_name || selectedDemo?.lead?.company_name || 'Unknown Company';
      
      const response = await dealsAPI.create({
        demo_id: newDeal.demo_id,
        company_name: companyName,
        plan_id: newDeal.plan_id,
        total_users: newDeal.total_users,
        deal_value_estimate: totals.grandTotal,
        notes: newDeal.notes,
        line_items: [
          { description: 'Plan Subscription', quantity: 1, unit_price: totals.planAmount },
          ...newDeal.addon_items.map(item => ({ description: item.name, quantity: 1, unit_price: item.price })),
          ...(newDeal.one_time_setup_charge > 0 ? [{ description: 'One-time Setup', quantity: 1, unit_price: newDeal.one_time_setup_charge }] : [])
        ],
        gst_percentage: 18
      });
      setDeals(prev => [response.data, ...prev]);
      // Update available SQDs
      setAvailableSQDs(prev => prev.filter(sqd => sqd.id !== newDeal.demo_id));
      setIsCreateModalOpen(false);
      setNewDeal({ demo_id: '', plan_id: '', total_users: 0, extra_user_rate: 0, addon_items: [], one_time_setup_charge: 0, notes: '' });
    } catch (error) {
      console.error('Failed to create deal:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to create deal',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDeleteDeal = async () => {
    if (!dealToDelete) return;
    try {
      await dealsAPI.delete(dealToDelete.id);
      setDeals(prev => prev.filter(d => d.id !== dealToDelete.id));
      setDeleteDialogOpen(false);
      setDealToDelete(null);
    } catch (error) {
      console.error('Failed to delete deal:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to delete deal',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleApproveDeal = async (approvalStage) => {
    if (!dealToApprove) return;
    try {
      await dealsAPI.approve(dealToApprove.id, approvalStage);
      
      // Update local state
      // We need to fetch the updated deal to get new fields (incentives etc)
      // but for UI responsiveness we can just update stage temporarily
      setDeals(prev => prev.map(deal => 
        deal.id === dealToApprove.id ? { ...deal, deal_stage: approvalStage, close_probability: 100 } : deal
      ));
      
      setApprovalDialogOpen(false);
      setDealToApprove(null);
    } catch (error) {
      console.error('Failed to approve deal:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to approve deal',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const getEmployeeName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unassigned';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading deals...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100">Deals Pipeline</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Track and manage sales opportunities</p>
        </div>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto" onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Deal
        </Button>
      </div>

      {/* Stats Cards - Mobile Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">Pipeline</p>
                <p className="text-sm sm:text-lg font-bold text-slate-800 dark:text-slate-100 truncate">{formatCurrency(totalPipeline)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">Won</p>
                <p className="text-sm sm:text-lg font-bold text-slate-800 dark:text-slate-100">{wonDeals.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Pending Approval</p>
                <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{pendingApprovalDeals.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Target className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Available SQDs</p>
                <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{availableSQDs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                <Clock className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Avg Close Time</p>
                <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{avgCloseTime || '-'} days</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-center gap-4 flex-1 w-full">
          {/* Search */}
          <div className="relative flex-1 w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search company/contact..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white dark:bg-slate-800"
            />
          </div>

          {/* Stage Filter */}
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-full sm:w-[180px] bg-white dark:bg-slate-800">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {ALL_DEAL_STAGES.map(stage => (
                <SelectItem key={stage.value} value={stage.value}>
                  <div className="flex items-center gap-2">
                    <div className={cn('w-2 h-2 rounded-full', stage.color)} />
                    {stage.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Assigned User Filter */}
          <Select value={assignedFilter} onValueChange={setAssignedFilter}>
            <SelectTrigger className="w-full sm:w-[180px] bg-white dark:bg-slate-800">
              <Target className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="All Owners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Owners</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Date Range */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Input 
                type="date" 
                value={dateRange.from} 
                onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
                className="w-full sm:w-auto bg-white dark:bg-slate-800"
            />
            <span className="text-slate-400">-</span>
            <Input 
                type="date" 
                value={dateRange.to} 
                onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
                className="w-full sm:w-auto bg-white dark:bg-slate-800"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-2 border rounded-lg p-1 bg-white dark:bg-slate-800 self-end xl:self-auto">
          <Button
            variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('kanban')}
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main Content: Kanban or List */}
      {viewMode === 'kanban' ? (
        <div className="grid grid-cols-6 gap-3 overflow-x-auto pb-4">
            {dealsByStage.map(({ stage, stageInfo, deals }) => (
                <div key={stage} className="min-w-[240px]">
                    {/* Simplified Kanban Card rendering for brevity, mimicking original */}
                    <div className={cn('p-2.5 rounded-t-lg text-white font-medium flex items-center justify-between', stageInfo.color)}>
                        <span className="text-sm truncate">{stageInfo.label}</span>
                        <Badge className="bg-white/20 text-white text-xs">{deals.length}</Badge>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-900 rounded-b-lg p-2 space-y-2 min-h-[350px] max-h-[500px] overflow-y-auto">
                        {deals.map(deal => (
                            <div 
                                key={deal.id} 
                                className="bg-white p-3 rounded shadow-sm border cursor-pointer hover:shadow-md"
                                onClick={() => navigate(`/deals/${deal.id}`)}
                            >
                                <p className="font-medium text-sm truncate">{deal.company_name}</p>
                                <p className="text-xs text-slate-500">{formatCurrency(deal.deal_value_estimate)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
      ) : (
        <DealsTable 
          deals={filteredDeals} 
          getStageInfo={getStageInfo}
          getEmployeeName={getEmployeeName}
          canApprove={canApprove}
          canDelete={canDelete}
          onApprove={(d) => { setDealToApprove(d); setApprovalDialogOpen(true); }}
          onDelete={(d) => { setDealToDelete(d); setDeleteDialogOpen(true); }}
          onEdit={(d) => { setDealToEdit(d); setIsEditModalOpen(true); }}
          onRefresh={fetchData}
        />
      )}

      {/* Create Deal Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Deal</DialogTitle>
            <DialogDescription>Create a deal from a qualified demo (SQD).</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Select Qualified Demo */}
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Select Qualified Demo (SQD) *</Label>
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 text-xs text-blue-600" 
                    onClick={fetchAvailableSQDs}
                    disabled={loadingSQD}
                >
                    {loadingSQD ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh List"}
                </Button>
              </div>
              <Select value={newDeal.demo_id} onValueChange={(v) => setNewDeal({...newDeal, demo_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a qualified demo" />
                </SelectTrigger>
                <SelectContent>
                  {availableSQDs.length === 0 ? (
                    <SelectItem value="none" disabled>No qualified demos available</SelectItem>
                  ) : (
                    availableSQDs.map(sqd => (
                      <SelectItem key={sqd.id} value={sqd.id}>
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-emerald-500" />
                          <span className="font-medium">{sqd.company_name}</span>
                          <span className="text-slate-400">-</span>
                          <span className="text-slate-500">{sqd.contact_name}</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Plan Selection */}
            <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Plan & Pricing</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Select Plan *</Label>
                  <Select value={newDeal.plan_id} onValueChange={handleSelectPlan}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map(plan => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.plan_name} - {formatCurrency(plan.base_price_per_month)}/mo
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedPlan && (
                    <p className="text-xs text-slate-500">
                      Base: {selectedPlan.base_user_capacity} users
                    </p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label>Total Users *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newDeal.total_users || ''}
                    onChange={(e) => setNewDeal({...newDeal, total_users: parseInt(e.target.value) || 0})}
                    placeholder="Enter user count"
                  />
                  {extraUsers > 0 && (
                    <p className="text-xs text-amber-600 font-medium">
                      +{extraUsers} extra users
                    </p>
                  )}
                </div>
              </div>

              {extraUsers > 0 && (
                <div className="mt-4 p-3 bg-amber-50 rounded border border-amber-200">
                  <Label className="text-amber-700">Per Extra User Rate (per month) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={newDeal.extra_user_rate || ''}
                    onChange={(e) => setNewDeal({...newDeal, extra_user_rate: parseInt(e.target.value) || 0})}
                    placeholder="Enter rate"
                    className="mt-1"
                  />
                </div>
              )}

              <div className="mt-4 p-3 bg-white dark:bg-slate-800 rounded border">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Base Plan Price</span>
                    <span className="font-medium">{formatCurrency(totals.basePlanPrice)}</span>
                  </div>
                  {extraUsers > 0 && (
                    <div className="flex justify-between text-amber-600">
                      <span>Extra Users ({extraUsers} x {formatCurrency(extraUserRate)})</span>
                      <span className="font-medium">{formatCurrency(totals.extraUsersCost)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-semibold">Plan Total</span>
                    <span className="font-bold text-emerald-600">{formatCurrency(totals.planAmount)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Addon Items */}
            <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Addon Items</h3>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <Input
                  placeholder="Addon name"
                  value={addonItem.name}
                  onChange={(e) => setAddonItem({...addonItem, name: e.target.value})}
                />
                <Input
                  type="number"
                  placeholder="Price"
                  value={addonItem.price || ''}
                  onChange={(e) => setAddonItem({...addonItem, price: parseInt(e.target.value) || 0})}
                />
                <Button variant="outline" onClick={addAddonItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {newDeal.addon_items.length > 0 && (
                <div className="space-y-1">
                  {newDeal.addon_items.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded">
                      <span className="text-sm">{item.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-emerald-600">{formatCurrency(item.price)}</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => removeAddonItem(item.id)}>x</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Setup Charge */}
            <div className="grid gap-2">
              <Label>One-Time Setup Charge</Label>
              <Input
                type="number"
                value={newDeal.one_time_setup_charge || ''}
                onChange={(e) => setNewDeal({...newDeal, one_time_setup_charge: parseInt(e.target.value) || 0})}
                placeholder="Enter setup charge"
              />
            </div>

            {/* Totals */}
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200">
              <h3 className="text-sm font-semibold text-emerald-800 mb-3">Deal Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Plan ({selectedPlan?.plan_name || 'N/A'})</span>
                  <span className="font-medium">{formatCurrency(totals.planAmount)}</span>
                </div>
                {totals.addonsTotal > 0 && (
                  <div className="flex justify-between">
                    <span>Addons</span>
                    <span className="font-medium">{formatCurrency(totals.addonsTotal)}</span>
                  </div>
                )}
                {newDeal.one_time_setup_charge > 0 && (
                  <div className="flex justify-between">
                    <span>Setup Charge</span>
                    <span className="font-medium">{formatCurrency(newDeal.one_time_setup_charge)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-emerald-200">
                  <span>Subtotal</span>
                  <span className="font-semibold">{formatCurrency(totals.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST (18%)</span>
                  <span className="font-medium">{formatCurrency(totals.gstAmount)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-emerald-200">
                  <span className="font-semibold text-emerald-800">Grand Total</span>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(totals.grandTotal)}</span>
                </div>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Notes</Label>
              <Textarea
                value={newDeal.notes}
                onChange={(e) => setNewDeal({...newDeal, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateDeal} className="bg-emerald-600 hover:bg-emerald-700">Create Deal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Deal Modal */}
      <EditDealDialog
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        deal={dealToEdit}
        onSuccess={() => {
            fetchData();
            setIsEditModalOpen(false);
            setDealToEdit(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDeal} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approval Dialog */}
      <AlertDialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Deal</AlertDialogTitle>
            <AlertDialogDescription>
              Approve deal for <strong>{dealToApprove?.company_name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleApproveDeal('WON_PAYMENT_PENDING')} 
              className="bg-amber-600 hover:bg-amber-700"
            >
              Approve (Payment Pending)
            </AlertDialogAction>
            <AlertDialogAction 
              onClick={() => handleApproveDeal('WON_PAYMENT_RECEIVED')} 
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Approve (Payment Received)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DealsPage;
